import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, Box, Grid,
    Card, CardContent, Divider, FormControlLabel, Switch, useTheme, useMediaQuery, Chip
} from '@mui/material';
import { 
    Edit, Delete, Add, PersonAdd, DeleteOutline, CloudUpload, 
    PhotoCamera, NotificationsActive, Close, Event, LocationOn, Star
} from '@mui/icons-material';
import api from '../../utils/api';
import { snackbar, successMessage } from '../../utils/showMessage';
import { Avatar, Autocomplete } from '@mui/material';

const textFieldStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
        },
    }
};

const RetrainingManagement = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [courses, setCourses] = useState([]);
    const [specialities, setSpecialities] = useState([]);
    const [users, setUsers] = useState([]);
    const [open, setOpen] = useState(false);
    const [currentCourse, setCurrentCourse] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        organizer: '',
        date: '',
        time: '',
        location: '',
        registrationLink: '',
        score: '',
        secretary: '',
        targetGroups: [],
        speakers: [],
        poster: '',
        sendNotification: false
    });

    useEffect(() => {
        fetchCourses();
        fetchSpecialities();
        fetchUsers();
    }, []);

    const fetchCourses = async () => {
        try {
            const response = await api.get('/retraining');
            setCourses(response.data);
        } catch (error) {
            snackbar('خطا در بارگذاری اطلاعات');
        }
    };

    const fetchSpecialities = async () => {
        try {
            const response = await api.get('/specialities');
            setSpecialities(response.data);
        } catch (error) {
            console.error('Error fetching specialities:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users');
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleOpen = (course = null) => {
        if (course) {
            setCurrentCourse(course);
            setFormData({
                ...course,
                targetGroups: course.targetGroups.map(g => g._id || g),
                score: course.score || '',
                poster: course.poster || '',
                sendNotification: false
            });
        } else {
            setCurrentCourse(null);
            setFormData({
                title: '',
                organizer: '',
                date: '',
                time: '',
                location: '',
                registrationLink: '',
                score: '',
                secretary: '',
                targetGroups: [],
                speakers: [],
                poster: '',
                sendNotification: false
            });
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSpeakerChange = (index, field, value) => {
        const newSpeakers = [...formData.speakers];
        newSpeakers[index][field] = value;
        setFormData({ ...formData, speakers: newSpeakers });
    };

    const handleSpeakerUserSelect = (index, user) => {
        if (!user) return;
        const newSpeakers = [...formData.speakers];
        newSpeakers[index] = {
            ...newSpeakers[index],
            name: `${user.firstName} ${user.lastName}`,
            image: user.profileImage, // Use existing profile image
            title: user.title || ''
        };
        setFormData({ ...formData, speakers: newSpeakers });
    };

    const handleSpeakerImageUpload = async (index, file) => {
        if (!file) return;
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        try {
            const response = await api.post('/retraining/upload-speaker-image', formDataUpload);
            const newSpeakers = [...formData.speakers];
            newSpeakers[index].image = response.data.filename;
            setFormData({ ...formData, speakers: newSpeakers });
            successMessage('تصویر با موفقیت بارگذاری شد');
        } catch (error) {
            snackbar('خطا در بارگذاری تصویر');
        }
    };

    const handlePosterUpload = async (file) => {
        if (!file) return;
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        try {
            const response = await api.post('/retraining/upload-poster', formDataUpload);
            setFormData({ ...formData, poster: response.data.filename });
            successMessage('پوستر با موفقیت بارگذاری شد');
        } catch (error) {
            snackbar('خطا در بارگذاری پوستر');
        }
    };

    const addSpeaker = () => {
        setFormData({
            ...formData,
            speakers: [...formData.speakers, { name: '', title: '', subject: '', image: '' }]
        });
    };

    const removeSpeaker = (index) => {
        const newSpeakers = formData.speakers.filter((_, i) => i !== index);
        setFormData({ ...formData, speakers: newSpeakers });
    };

    const handleSubmit = async () => {
        try {
            if (currentCourse) {
                const { sendNotification, ...updatePayload } = formData;
                await api.put(`/retraining/${currentCourse._id}`, updatePayload);
                successMessage('با موفقیت بروزرسانی شد');
            } else {
                await api.post('/retraining', formData);
                successMessage('با موفقیت ایجاد شد');
            }
            fetchCourses();
            handleClose();
        } catch (error) {
            snackbar('خطا در ذخیره اطلاعات');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('آیا از حذف این مورد اطمینان دارید؟')) {
            try {
                await api.delete(`/retraining/${id}`);
                successMessage('با موفقیت حذف شد');
                fetchCourses();
            } catch (error) {
                snackbar('خطا در حذف');
            }
        }
    };

    const handleResendNotification = async (id) => {
        if (!window.confirm('ارسال مجدد نوتیفیکیشن برای این دوره انجام شود؟')) return;
        try {
            await api.post(`/retraining/${id}/send-notification`);
            successMessage('نوتیفیکیشن ارسال شد');
        } catch (error) {
            console.error('Error sending notification:', error);
            snackbar(error?.response?.data?.message || 'خطا در ارسال نوتیفیکیشن');
        }
    };

    const getImageUrl = (filename) => {
        if (!filename) return '';
        if (filename.startsWith('http')) return filename;
        return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/profile/${filename}`;
    };

    const getPosterUrl = (filename) => {
        if (!filename) return '';
        return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${filename}`;
    };

    return (
        <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
            <Box 
                display="flex" 
                flexDirection={isMobile ? 'column' : 'row'} 
                justifyContent="space-between" 
                alignItems={isMobile ? 'stretch' : 'center'} 
                gap={2}
                mb={3}
            >
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">
                    مدیریت دوره‌های بازآموزی
                </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<Add />} 
                    onClick={() => handleOpen()}
                    fullWidth={isMobile}
                    sx={{ borderRadius: 3, px: 3 }}
                >
                    افزودن دوره جدید
                </Button>
            </Box>

            {isMobile ? (
                <Grid container spacing={2}>
                    {courses.map((course) => (
                        <Grid item xs={12} key={course._id}>
                            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', position: 'relative', overflow: 'visible' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box display="flex" gap={2}>
                                        <Avatar 
                                            src={getPosterUrl(course.poster)} 
                                            variant="rounded" 
                                            sx={{ width: 80, height: 110, borderRadius: 2, boxShadow: 2 }} 
                                        />
                                        <Box flex={1}>
                                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                                                {course.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                <LocationOn sx={{ fontSize: 16 }} /> {course.organizer}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                                <Event sx={{ fontSize: 16 }} /> {course.date}
                                            </Typography>
                                            <Chip 
                                                label={`${course.score} امتیاز`} 
                                                size="small" 
                                                color="primary" 
                                                variant="outlined" 
                                                icon={<Star sx={{ fontSize: '14px !important' }} />}
                                                sx={{ mt: 1, borderRadius: 1 }}
                                            />
                                        </Box>
                                    </Box>
                                    
                                    <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />
                                    
                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                        <IconButton 
                                            size="small" 
                                            sx={{ bgcolor: 'info.light', color: 'info.contrastText', '&:hover': { bgcolor: 'info.main' } }}
                                            onClick={() => handleResendNotification(course._id)}
                                            title="ارسال مجدد نوتیفیکیشن"
                                        >
                                            <NotificationsActive fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.main' } }}
                                            onClick={() => handleOpen(course)}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton 
                                            size="small" 
                                            sx={{ bgcolor: 'error.light', color: 'error.contrastText', '&:hover': { bgcolor: 'error.main' } }}
                                            onClick={() => handleDelete(course._id)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>پوستر</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>عنوان</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>برگزارکننده</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>تاریخ</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>امتیاز</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>عملیات</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {courses.map((course) => (
                                <TableRow key={course._id} hover>
                                    <TableCell align="right">
                                        <Avatar src={getPosterUrl(course.poster)} variant="rounded" sx={{ width: 50, height: 70, borderRadius: 1.5, boxShadow: 1 }} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500 }}>{course.title}</TableCell>
                                    <TableCell align="right" color="text.secondary">{course.organizer}</TableCell>
                                    <TableCell align="right">{course.date}</TableCell>
                                    <TableCell align="right">
                                        <Chip label={course.score} size="small" variant="outlined" color="primary" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" justifyContent="center" gap={0.5}>
                                            <IconButton color="info" onClick={() => handleResendNotification(course._id)} title="ارسال نوتیفیکیشن">
                                                <NotificationsActive fontSize="small" />
                                            </IconButton>
                                            <IconButton color="primary" onClick={() => handleOpen(course)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => handleDelete(course._id)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog 
                open={open} 
                onClose={handleClose} 
                maxWidth="md" 
                fullWidth 
                fullScreen={isMobile}
                PaperProps={{
                    sx: { borderRadius: isMobile ? 0 : 3 }
                }}
            >
                <DialogTitle sx={{ 
                    bgcolor: isMobile ? 'primary.main' : 'transparent', 
                    color: isMobile ? 'white' : 'text.primary',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2
                }}>
                    <Typography variant="h6" fontWeight="bold">
                        {currentCourse ? 'ویرایش دوره' : 'افزودن دوره جدید'}
                    </Typography>
                    {isMobile && (
                        <IconButton color="inherit" onClick={handleClose} size="small">
                            <Close />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent dividers={isMobile}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                            <Box 
                                display="flex" 
                                flexDirection="column" 
                                alignItems="center" 
                                justifyContent="center" 
                                height="100%" 
                                border="2px dashed"
                                borderColor="divider"
                                borderRadius={3} 
                                p={2}
                                sx={{ bgcolor: 'grey.50' }}
                            >
                                {formData.poster ? (
                                    <img src={getPosterUrl(formData.poster)} alt="Poster" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, marginBottom: 10, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                                ) : (
                                    <Typography variant="body2" color="textSecondary" mb={1}>تصویر پوستر</Typography>
                                )}
                                <Button variant="outlined" component="label" startIcon={<CloudUpload />} size="small" sx={{ borderRadius: 2 }}>
                                    {formData.poster ? 'تغییر پوستر' : 'بارگذاری پوستر'}
                                    <input type="file" hidden accept="image/*" onChange={(e) => handlePosterUpload(e.target.files[0])} />
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="عنوان دوره" name="title" value={formData.title} onChange={handleChange} sx={textFieldStyle} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="برگزارکننده" name="organizer" value={formData.organizer} onChange={handleChange} sx={textFieldStyle} />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField
                                fullWidth
                                name="date"
                                label="تاریخ برگزاری"
                                value={formData.date}
                                onChange={handleChange}
                                placeholder="1403/11/05"
                                sx={textFieldStyle}
                            />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="ساعت" name="time" value={formData.time} onChange={handleChange} placeholder="۰۹:۰۰ الی ۱۶:۰۰" sx={textFieldStyle} />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField fullWidth label="امتیاز" name="score" type="number" value={formData.score} onChange={handleChange} sx={textFieldStyle} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="مکان/لینک برگزاری" name="location" value={formData.location} onChange={handleChange} sx={textFieldStyle} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="لینک ثبت‌نام" name="registrationLink" value={formData.registrationLink} onChange={handleChange} sx={textFieldStyle} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField fullWidth label="دبیر علمی" name="secretary" value={formData.secretary} onChange={handleChange} sx={textFieldStyle} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Switch checked={formData.sendNotification} onChange={handleChange} name="sendNotification" disabled={!!currentCourse} color="primary" />}
                                label="ارسال نوتیفیکیشن برای کاربران هدف"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                select
                                fullWidth
                                label="گروه‌های هدف"
                                SelectProps={{ multiple: true }}
                                name="targetGroups"
                                value={formData.targetGroups}
                                onChange={handleChange}
                                sx={textFieldStyle}
                                helperText="رشته‌هایی که این دوره برای آنها امتیاز دارد را انتخاب کنید"
                            >
                                {specialities.map((spec) => (
                                    <MenuItem key={spec._id} value={spec._id}>
                                        {spec.title} ({spec.level})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 3 }} />
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Typography variant="h6" fontWeight="bold" color="primary">سخنرانان</Typography>
                                <Button 
                                    size="small" 
                                    variant="outlined"
                                    startIcon={<PersonAdd />} 
                                    onClick={addSpeaker}
                                    sx={{ borderRadius: 2 }}
                                >
                                    افزودن سخنران
                                </Button>
                            </Box>
                            {formData.speakers.map((speaker, index) => (
                                <Card key={index} variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 3, bgcolor: 'background.paper', borderStyle: 'solid', borderColor: 'divider' }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm="auto" sx={{ display: 'flex', justifyContent: isMobile ? 'center' : 'flex-start' }}>
                                            <Box position="relative">
                                                <Avatar src={getImageUrl(speaker.image)} sx={{ width: 80, height: 80, boxShadow: 2, border: '2px solid #fff' }} />
                                                <IconButton
                                                    size="small"
                                                    component="label"
                                                    sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, boxShadow: 2 }}
                                                >
                                                    <PhotoCamera fontSize="small" />
                                                    <input type="file" hidden accept="image/*" onChange={(e) => handleSpeakerImageUpload(index, e.target.files[0])} />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Autocomplete
                                                options={users}
                                                getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.phone})`}
                                                renderInput={(params) => <TextField {...params} label="انتخاب از کاربران" size="small" sx={textFieldStyle} />}
                                                onChange={(e, val) => handleSpeakerUserSelect(index, val)}
                                                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
                                            />
                                            <TextField fullWidth size="small" label="نام سخنران (دستی)" value={speaker.name} onChange={(e) => handleSpeakerChange(index, 'name', e.target.value)} sx={textFieldStyle} />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField fullWidth size="small" label="عنوان" value={speaker.title} onChange={(e) => handleSpeakerChange(index, 'title', e.target.value)} sx={textFieldStyle} />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField fullWidth size="small" label="موضوع" value={speaker.subject} onChange={(e) => handleSpeakerChange(index, 'subject', e.target.value)} sx={textFieldStyle} />
                                        </Grid>
                                        {!isMobile ? (
                                            <Grid item xs={12} sm="auto">
                                                <IconButton color="error" onClick={() => removeSpeaker(index)} sx={{ bgcolor: 'error.lighter' }}>
                                                    <DeleteOutline />
                                                </IconButton>
                                            </Grid>
                                        ) : (
                                            <Grid item xs={12}>
                                                <Button 
                                                    fullWidth 
                                                    color="error" 
                                                    variant="text" 
                                                    startIcon={<DeleteOutline />} 
                                                    onClick={() => removeSpeaker(index)}
                                                >
                                                    حذف سخنران
                                                </Button>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Card>
                            ))}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: isMobile ? 'grey.50' : 'transparent' }}>
                    <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>انصراف</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" sx={{ borderRadius: 2, px: 3 }}>
                        {currentCourse ? 'بروزرسانی' : 'ذخیره'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};


export default RetrainingManagement;
